import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp, FaPlus, FaPrint, FaSave, FaTrash } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import Layout from "../../components/Layout";
import api from "../../services/api";
import { crm, Pill } from "../../theme";
import { NcmTaxRate } from "../../models/Tax";
import { getNcmTaxRate } from "../../services/taxService";
import { addQuoteVersion, createQuote, getQuote, updateQuote } from "../../services/quoteService";
import { QuoteDetail, QuoteVersion } from "../../models/Quote";
import QuoteStatusPill from "../../components/QuoteStatusPill";
import SaveQuoteModal, { QuoteFormValues } from "./SaveQuoteModal";
import { TableCard, tdProps, thProps } from "../process/components/tableStyles";
import { toneColors } from "../process/components/duimp";
import {
  afrmmFee,
  calculateContaOrdem,
  ContaOrdemParams,
  CUSTOM_PORT,
  DEFAULT_COFINS_RATE,
  DEFAULT_PIS_RATE,
  defaultParams,
  effectiveIcmsParams,
  insuranceOf,
  Modal,
  PORTS,
  Regime,
  regimeLabels,
  siscomexFee,
  storageFee,
} from "./contaOrdem";
import EstimateSheet, { EstimateExpense, EstimateHeader, formatMoney } from "./EstimateSheet";
import { SimulatorState } from "./quoteState";
import ClientBranchPicker, { ClientSelection, emptySelection } from "./ClientBranchPicker";
import { collectedIcmsRate, effectiveIcmsRate, resolveRates } from "./fiscal";
import EncomendaSimulator from "./EncomendaSimulator";

type SimulatorMode = "contaOrdem" | "encomenda";

type Currency = "USD" | "EUR" | "CNY";

// Rascunhos em texto: o usuário digita "1.234,56" / "18,5" livremente
interface ItemDraft {
  key: number;
  ncm: string;
  description: string;
  reference: string;
  quantity: string;
  unitValue: string;
  unitWeight: string;
  ii: string;
  ipi: string;
  pis: string;
  cofins: string;
  dumping: string;
  /** Crédito de ICMS / recolhimento (%) do item, da parametrização; vazio = parâmetros gerais. */
  icmsCredit?: string;
  icmsCollect?: string;
  rates?: NcmTaxRate | null;
}

type ParamDrafts = Record<keyof ContaOrdemParams, string>;

/** "1.234,56" | "1234.56" -> número; vazio -> null */
const parseNumber = (value: string): number | null => {
  const text = value.trim();
  if (!text) return null;
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const num = (value: string) => parseNumber(value) ?? 0;
const toDraft = (value?: number | null) => (value == null ? "" : String(value).replace(".", ","));
const fmt = (value: number, digits = 2) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

let nextKey = 1;
const newItem = (): ItemDraft => ({
  key: nextKey++,
  ncm: "",
  description: "",
  reference: "",
  quantity: "1",
  unitValue: "",
  unitWeight: "",
  ii: "",
  ipi: "",
  pis: toDraft(DEFAULT_PIS_RATE),
  cofins: toDraft(DEFAULT_COFINS_RATE),
  dumping: "",
  icmsCredit: "",
  icmsCollect: "",
});

const round4 = (value: number) => Math.round(value * 10000) / 10000;

const initialParams = Object.fromEntries(
  Object.entries(defaultParams).map(([key, value]) => [key, toDraft(value)])
) as ParamDrafts;

const today = () => new Date();
const suggestedNumber = () => {
  const d = today();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `CEO${pad(d.getDate())}${pad(d.getMonth() + 1)}${d.getFullYear()}-`;
};

const Card = ({ title, subtitle, children, action }: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode }) => (
  <Box w="95%" bg="white" borderRadius={crm.radius} border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} p="24px" mb="20px">
    <Flex justify="space-between" align="flex-start" gap="12px">
      <Box>
        <Text fontSize="16px" fontWeight="700" color={crm.text}>
          {title}
        </Text>
        {subtitle && (
          <Text fontSize="13px" color={crm.textMuted} mt="2px">
            {subtitle}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    <Box mt="16px">{children}</Box>
  </Box>
);

const Field = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <Box>
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">
      {label}
    </Text>
    {children}
    {hint && (
      <Text fontSize="11px" color={crm.textFaint} mt="2px">
        {hint}
      </Text>
    )}
  </Box>
);

const Stat = ({ label, value, highlight, hint }: { label: string; value: string; highlight?: boolean; hint?: string }) => (
  <Box
    p="14px 16px"
    borderRadius={crm.radiusSm}
    border={`1px solid ${highlight ? "#c7d7fe" : crm.border}`}
    bg={highlight ? crm.brandSoft : crm.subtle}
  >
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>
      {label}
    </Text>
    <Text fontSize="20px" fontWeight="700" color={highlight ? crm.brandDark : crm.text} mt="4px">
      {value}
    </Text>
    {hint && (
      <Text fontSize="11px" color={crm.textFaint}>
        {hint}
      </Text>
    )}
  </Box>
);

/**
 * Simulador de importação por conta e ordem (equipe CVF): versão sistema da planilha
 * "CONTA E ORDEM ... CEO". Recalcula ao vivo no front (contaOrdem.ts). O cálculo só é gravado ao salvar como orçamento (api/quote).
 */
const Simulator = () => {
  // Conta e ordem (planilha CEO) ou Encomenda (planilha NF ALFA ENCOMENDA, com ST da filial)
  const [mode, setMode] = useState<SimulatorMode>("contaOrdem");
  const [selection, setSelection] = useState<ClientSelection>(emptySelection);
  // Cabeçalho da estimativa
  const [number, setNumber] = useState(suggestedNumber);
  const [client, setClient] = useState("");
  const [product, setProduct] = useState("");
  const [origin, setOrigin] = useState("");
  const [modal, setModal] = useState<Modal>("MARITIMO");
  const [incoterm, setIncoterm] = useState("FOB");
  const [regime, setRegime] = useState<Regime>("PRESUMIDO");
  const [equipment, setEquipment] = useState("");

  // Operação
  const [currency, setCurrency] = useState<Currency>("USD");
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [exchange, setExchange] = useState("");
  const [freight, setFreight] = useState("");
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);

  // Despesas (R$)
  const [portId, setPortId] = useState(CUSTOM_PORT);
  const [storageFirst, setStorageFirst] = useState(toDraft(PORTS[0].firstPeriodRate));
  const [storageFixed, setStorageFixed] = useState(toDraft(PORTS[0].fixedBrl));
  const [storageExtraRate, setStorageExtraRate] = useState(toDraft(PORTS[0].extraPeriodRate));
  const [storageExtraPeriods, setStorageExtraPeriods] = useState("0");
  const [storageManual, setStorageManual] = useState("");
  const [agentFees, setAgentFees] = useState("4.200,00");
  const [brokerFees, setBrokerFees] = useState("1.200,00");
  const [nationalFreight, setNationalFreight] = useState("");
  const [daytona, setDaytona] = useState("");
  const [license, setLicense] = useState("");
  const [afrmmManual, setAfrmmManual] = useState("");
  const [additions, setAdditions] = useState("");
  const [siscomexManual, setSiscomexManual] = useState("");

  const [paramDrafts, setParamDrafts] = useState<ParamDrafts>(initialParams);
  const [showParams, setShowParams] = useState(false);

  // Orçamento (CRM) aberto via /simulador?quote=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const quoteParam = searchParams.get("quote");
  const toast = useToast();
  const quoteModal = useDisclosure();
  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [quoteVersion, setQuoteVersion] = useState<number>(0);
  const [savingVersion, setSavingVersion] = useState(false);
  // Troca de versão com alterações não salvas pede confirmação
  const discardDialog = useDisclosure();
  const [pendingVersion, setPendingVersion] = useState<number | null>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const keepExchange = useRef(Boolean(quoteParam));

  useEffect(() => {
    // Mesma cotação exibida no restante do sistema (/api/process/currencies)
    api
      .get("/api/process/currencies")
      .then(({ data }) => {
        const rates = { USD: data.dollarBr, EUR: data.euroBr, CNY: data.yuenBr };
        setExchangeRates(rates);
        if (!keepExchange.current) setExchange(rates.USD ? rates.USD.toFixed(4).replace(".", ",") : "");
      })
      .catch(() => undefined);
  }, []);

  const buildState = (): SimulatorState => ({
    v: 1,
    number, client, product, origin, modal, incoterm, regime, equipment,
    currency, exchange, freight,
    items: items.map(({ key, rates, ...rest }) => rest),
    portId, storageFirst, storageFixed, storageExtraRate, storageExtraPeriods, storageManual,
    agentFees, brokerFees, nationalFreight, daytona, license, afrmmManual, additions, siscomexManual,
    paramDrafts,
  });

  const restoreState = (state: SimulatorState) => {
    keepExchange.current = true;
    setNumber(state.number ?? "");
    setClient(state.client ?? "");
    setProduct(state.product ?? "");
    setOrigin(state.origin ?? "");
    setModal(state.modal ?? "MARITIMO");
    setIncoterm(state.incoterm ?? "FOB");
    setRegime(state.regime ?? "PRESUMIDO");
    setEquipment(state.equipment ?? "");
    setCurrency((state.currency as Currency) ?? "USD");
    setExchange(state.exchange ?? "");
    setFreight(state.freight ?? "");
    setItems(state.items?.length ? state.items.map((i) => ({ ...i, key: nextKey++ })) : [newItem()]);
    setPortId(state.portId ?? CUSTOM_PORT);
    setStorageFirst(state.storageFirst ?? "");
    setStorageFixed(state.storageFixed ?? "");
    setStorageExtraRate(state.storageExtraRate ?? "");
    setStorageExtraPeriods(state.storageExtraPeriods ?? "0");
    setStorageManual(state.storageManual ?? "");
    setAgentFees(state.agentFees ?? "");
    setBrokerFees(state.brokerFees ?? "");
    setNationalFreight(state.nationalFreight ?? "");
    setDaytona(state.daytona ?? "");
    setLicense(state.license ?? "");
    setAfrmmManual(state.afrmmManual ?? "");
    setAdditions(state.additions ?? "");
    setSiscomexManual(state.siscomexManual ?? "");
    setParamDrafts({ ...initialParams, ...(state.paramDrafts ?? {}) });
  };

  const applyVersion = (loaded: QuoteDetail, versionNumber: number) => {
    const version = loaded.versions.find((v: QuoteVersion) => v.version === versionNumber);
    if (!version) return;
    try {
      restoreState(JSON.parse(version.inputJson) as SimulatorState);
      // O CVF Nº impresso é o número do orçamento, não o rascunho salvo na versão
      if (loaded.number) setNumber(loaded.number);
      setQuoteVersion(version.version);
    } catch {
      toast({ status: "error", title: "Não foi possível restaurar esta versão do orçamento." });
    }
  };

  /** Estado atual difere da versão aberta (compara com o inputJson salvo, CVF Nº = número do orçamento). */
  const hasUnsavedChanges = () => {
    const version = quote?.versions.find((v: QuoteVersion) => v.version === quoteVersion);
    if (!quote || !version) return false;
    try {
      const saved = { ...(JSON.parse(version.inputJson) as SimulatorState), number: quote.number ?? "" };
      return JSON.stringify(buildState()) !== JSON.stringify(saved);
    } catch {
      return false;
    }
  };

  const changeVersion = (versionNumber: number) => {
    if (!quote || versionNumber === quoteVersion) return;
    if (hasUnsavedChanges()) {
      setPendingVersion(versionNumber);
      discardDialog.onOpen();
      return;
    }
    applyVersion(quote, versionNumber);
  };

  const confirmDiscard = () => {
    discardDialog.onClose();
    if (quote && pendingVersion != null) applyVersion(quote, pendingVersion);
    setPendingVersion(null);
  };

  // Último orçamento pedido: resposta de um pedido anterior (troca rápida de ?quote) é ignorada
  const latestQuoteRequest = useRef<string | null>(null);

  const loadQuote = async (id: string, versionNumber?: number) => {
    latestQuoteRequest.current = id;
    const loaded = await getQuote(id);
    if (latestQuoteRequest.current !== id) return;
    if (!loaded) {
      toast({ status: "error", title: "Orçamento não encontrado." });
      setSearchParams({}, { replace: true });
      return;
    }
    setQuote(loaded);
    applyVersion(loaded, versionNumber ?? loaded.currentVersion);
  };

  useEffect(() => {
    // Orçamento salvo é sempre conta e ordem: abrir ?quote= volta para esse modo
    if (quoteParam) setMode("contaOrdem");
    if (quoteParam && quoteParam !== quote?.id) loadQuote(quoteParam);
    if (!quoteParam) {
      latestQuoteRequest.current = null;
      setQuote(null);
    }
  }, [quoteParam]);

  const changeCurrency = (next: Currency) => {
    setCurrency(next);
    const rate = exchangeRates[next];
    setExchange(rate ? rate.toFixed(4).replace(".", ",") : "");
  };

  const params = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(paramDrafts).map(([key, value]) => [key, parseNumber(value) ?? 0])
      ) as unknown as ContaOrdemParams,
    [paramDrafts]
  );

  const port = PORTS.find((p) => p.id === portId) ?? PORTS[0];
  const changePort = (id: string) => {
    const next = PORTS.find((p) => p.id === id) ?? PORTS[0];
    setPortId(next.id);
    setStorageFirst(toDraft(next.firstPeriodRate));
    setStorageFixed(toDraft(next.fixedBrl));
    setStorageExtraRate(toDraft(next.extraPeriodRate));
  };

  const exchangeRate = num(exchange);
  const freightValue = num(freight);
  const itemInputs = items.map((i) => ({
    quantity: num(i.quantity),
    unitValue: num(i.unitValue),
    unitWeight: num(i.unitWeight),
    iiRate: num(i.ii),
    ipiRate: num(i.ipi),
    pisRate: num(i.pis),
    cofinsRate: num(i.cofins),
    dumping: num(i.dumping),
    icmsCreditRate: parseNumber(i.icmsCredit ?? ""),
    icmsCollectRate: parseNumber(i.icmsCollect ?? ""),
  }));

  // Despesas dependem só do CIF (não circular): FOB + frete + seguro
  const totalFob = itemInputs.reduce((sum, i) => sum + i.quantity * i.unitValue, 0);
  const cifBrl = (totalFob + freightValue + insuranceOf(totalFob, freightValue, params)) * exchangeRate;
  const distinctNcms = new Set(items.map((i) => i.ncm.replace(/\D/g, "")).filter(Boolean)).size;
  const autoAdditions = Math.max(distinctNcms, 1);

  const storageAuto = port.noData
    ? 0
    : storageFee(cifBrl, { firstPeriodRate: num(storageFirst), fixedBrl: num(storageFixed), extraPeriodRate: num(storageExtraRate) }, num(storageExtraPeriods));
  const afrmmAuto = afrmmFee(freightValue * exchangeRate, modal, params);
  const siscomexAuto = siscomexFee(parseNumber(additions) ?? autoAdditions);

  const expenses: EstimateExpense[] = [
    { label: "Armazenagem e movimentação no porto", amountBrl: parseNumber(storageManual) ?? storageAuto },
    { label: "Taxas de destino agente de carga", amountBrl: num(agentFees) },
    { label: "Honorários despachante", amountBrl: num(brokerFees) },
    { label: "AFRMM", amountBrl: parseNumber(afrmmManual) ?? afrmmAuto },
    { label: "Frete nacional", amountBrl: num(nationalFreight) },
    { label: "Daytona", amountBrl: num(daytona) },
    { label: "Licença de importação", amountBrl: num(license) },
    { label: "Taxa SISCOMEX", amountBrl: parseNumber(siscomexManual) ?? siscomexAuto },
  ];
  const expensesBrl = expenses.reduce((sum, e) => sum + e.amountBrl, 0);

  const result = calculateContaOrdem({ exchangeRate, freight: freightValue, regime, items: itemInputs, expensesBrl, params });
  // Base "por dentro" divide por (1 − crédito): 100% ou mais não tem resultado válido
  const creditInvalid = [params.icmsCreditRate, ...itemInputs.map((i) => i.icmsCreditRate ?? params.icmsCreditRate)]
    .some((rate) => rate < 0 || rate >= 100);
  const hasItemIcms = itemInputs.some((i) => i.icmsCreditRate != null || i.icmsCollectRate != null);
  // Itens com alíquota própria: a estimativa mostra a média ponderada
  const sheetParams = hasItemIcms ? effectiveIcmsParams(params, result.totals) : params;
  const ready = exchangeRate > 0 && totalFob > 0 && !creditInvalid;

  const round2 = (value: number) => Math.round(value * 100) / 100;
  const buildVersionPayload = () => {
    const totalsBrl = Object.fromEntries(
      Object.entries(result.totals).map(([key, value]) => [key, round2((value as number) * exchangeRate)])
    );
    return {
      inputJson: JSON.stringify(buildState()),
      resultJson: JSON.stringify({ currency, exchangeRate, totals: result.totals, totalsBrl, expenses }),
      currency,
      exchangeRate,
      cifBrl: totalsBrl.cif,
      taxesBrl: totalsBrl.taxes,
      expensesBrl: totalsBrl.expenses,
      disbursementBrl: totalsBrl.disbursement,
      finalCostBrl: totalsBrl.finalCost,
    };
  };

  const quoteInitial: QuoteFormValues = {
    number: quote?.number ?? number,
    clientName: quote?.clientName ?? client,
    prospectId: quote?.prospectId,
    companyId: quote?.companyId ?? selection.companyId,
    validUntil: quote?.validUntil ? quote.validUntil.slice(0, 10) : null,
    notes: quote?.notes ?? "",
  };

  const submitQuoteModal = async (values: QuoteFormValues): Promise<string[] | null> => {
    const validUntil = values.validUntil ? `${values.validUntil}T00:00:00` : null;
    const header = {
      clientName: values.clientName,
      prospectId: values.prospectId ?? null,
      companyId: values.companyId ?? null,
      product: product || null,
      validUntil,
      notes: values.notes || null,
    };
    const response = quote
      ? await updateQuote(quote.id!, header)
      : await createQuote({ ...header, number: values.number, ...buildVersionPayload() });
    if (!response.success) return response.notifications;

    setNumber(values.number);
    setClient(values.clientName);
    quoteModal.onClose();
    toast({ status: "success", title: quote ? "Dados do orçamento atualizados." : "Orçamento salvo." });
    if (quote) {
      const reloaded = await getQuote(quote.id!);
      if (reloaded) setQuote(reloaded);
    } else if (response.id) {
      setSearchParams({ quote: response.id }, { replace: true });
    }
    return null;
  };

  const saveNewVersion = async () => {
    if (!quote?.id) return;
    setSavingVersion(true);
    const response = await addQuoteVersion(quote.id, buildVersionPayload());
    setSavingVersion(false);
    if (!response.success) {
      toast({ status: "error", title: response.notifications[0] ?? "Não foi possível salvar a versão." });
      return;
    }
    toast({ status: "success", title: "Nova versão salva." });
    const reloaded = await getQuote(quote.id);
    if (reloaded) {
      setQuote(reloaded);
      setQuoteVersion(reloaded.currentVersion);
    }
  };

  const header: EstimateHeader = {
    number,
    client,
    product,
    origin,
    modal: modal === "MARITIMO" ? "Marítimo" : "Aéreo",
    incoterm,
    regime,
    equipment,
    date: today(),
  };
  const itemInfos = items.map((i, index) => ({
    description: i.description || i.ncm || `Item ${index + 1}`,
    ...itemInputs[index],
  }));

  const updateItem = (key: number, patch: Partial<ItemDraft>) =>
    setItems((list) => list.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  // Parametrização fiscal (conta e ordem): crédito = ICMS destacado; recolhe = crédito × (1 − crédito presumido)
  const coSetting = selection.settings.contaOrdem ?? null;
  const icmsFromSetting = (ncm: string): Partial<ItemDraft> => {
    if (!coSetting) return {};
    const { rates } = resolveRates(coSetting, { ncm, state: selection.branch?.state, customerName: selection.companyName });
    if (rates.icmsPercent == null) return {};
    return { icmsCredit: toDraft(round4(effectiveIcmsRate(rates))), icmsCollect: toDraft(round4(collectedIcmsRate(rates))) };
  };
  const previousSettingId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const id = coSetting?.id;
    if (id) setItems((list) => list.map((i) => ({ ...i, ...icmsFromSetting(i.ncm) })));
    // Tirou a parametrização: volta para os parâmetros gerais
    else if (previousSettingId.current) setItems((list) => list.map((i) => ({ ...i, icmsCredit: "", icmsCollect: "" })));
    previousSettingId.current = id;
  }, [coSetting?.id, selection.branch?.id, selection.companyName]);

  useEffect(() => {
    if (selection.companyName) setClient(selection.companyName);
  }, [selection.companyName]);

  // Tabela NCM (TEC/TIPI + exceções PIS/COFINS) pré-preenche as alíquotas; usuário pode editar
  const lookupNcm = async (item: ItemDraft) => {
    const digits = item.ncm.replace(/\D/g, "");
    if (digits.length !== 8) {
      updateItem(item.key, { rates: undefined });
      return;
    }
    if (item.rates?.ncm === digits) return;
    const fiscal = icmsFromSetting(digits);
    try {
      const rates = await getNcmTaxRate(digits);
      updateItem(item.key, {
        ...fiscal,
        rates: rates ?? null,
        description: item.description || rates?.description?.slice(0, 120) || "",
        ...(rates && {
          ii: toDraft(rates.iiRate ?? 0),
          ipi: toDraft(rates.ipiNotTaxed ? 0 : rates.ipiRate ?? 0),
          pis: toDraft(rates.pisRate ?? rates.defaultPisRate),
          cofins: toDraft(rates.cofinsRate ?? rates.defaultCofinsRate),
        }),
      });
    } catch {
      updateItem(item.key, { ...fiscal, rates: undefined });
    }
  };

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Estimativa ${number || ""} ${client || ""}`.trim(),
    pageStyle: `
      @page { size: A4; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `,
  });

  const input = (value: string, onChange: (v: string) => void, props: Record<string, any> = {}) => (
    <Input size="sm" bg="white" value={value} onChange={(e) => onChange(e.target.value)} {...props} />
  );

  const brlOf = (value: number) => formatMoney(value * exchangeRate, "BRL");

  return (
    <Box height="100%">
      <Layout>
        <Flex minHeight="100%" flexDirection="column" alignItems="center" backgroundColor="var(--main-bg-color)" pb="48px">
          <Flex width="95%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box flex="1">
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                Simulador · {mode === "contaOrdem" ? "conta e ordem" : "encomenda"}
              </Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">
                {mode === "contaOrdem"
                  ? "Estimativa de custos de importação por conta e ordem via trading. Recalcula enquanto você digita."
                  : "Custo de importação, preço de venda e NF de saída com ST (filial do cliente). Recalcula enquanto você digita."}
              </Text>
            </Box>
            {!quote && (
              <Flex bg={crm.subtle} p="4px" borderRadius="10px" border={`1px solid ${crm.border}`} gap="2px">
                {(["contaOrdem", "encomenda"] as SimulatorMode[]).map((m) => (
                  <Button
                    key={m}
                    size="sm"
                    variant="ghost"
                    bg={mode === m ? "white" : "transparent"}
                    color={mode === m ? crm.brand : crm.textMuted}
                    boxShadow={mode === m ? crm.shadowSm : undefined}
                    onClick={() => setMode(m)}
                    aria-pressed={mode === m}
                  >
                    {m === "contaOrdem" ? "Conta e ordem" : "Encomenda"}
                  </Button>
                ))}
              </Flex>
            )}
            {!quote && mode === "contaOrdem" && (
              <Button leftIcon={<FaSave />} bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!ready} onClick={quoteModal.onOpen}>
                Salvar como orçamento
              </Button>
            )}
            {mode === "contaOrdem" && (
              <Button leftIcon={<FaPrint />} variant="outline" isDisabled={!ready} onClick={() => handlePrint()}>
                Imprimir estimativa
              </Button>
            )}
          </Flex>

          <Card
            title="Cliente"
            subtitle="Cliente, filial (UF) e parametrização fiscal do Gett: definem ICMS, ST/MVA, DIFAL e IBS/CBS de cada item pelo NCM."
          >
            <ClientBranchPicker value={selection} kind={mode} onChange={(update) => setSelection(update)} />
          </Card>

          {/* Fica montado ao alternar o modo: não perde o que foi digitado */}
          <Box w="100%" display={mode === "encomenda" ? "flex" : "none"} flexDirection="column" alignItems="center">
            <EncomendaSimulator
              dollarRate={exchangeRates.USD}
              branch={selection.branch}
              setting={selection.settings.encomenda ?? null}
              customerName={selection.companyName}
            />
          </Box>
          {mode === "contaOrdem" && (<>

          {quote && (
            <Flex
              w="95%"
              mb="20px"
              p="12px 16px"
              bg="white"
              border={`1px solid ${crm.border}`}
              borderRadius={crm.radius}
              boxShadow={crm.shadowSm}
              align="center"
              gap="12px"
              wrap="wrap"
            >
              <Box>
                <Text fontSize="11px" fontWeight="600" color={crm.textMuted}>
                  Orçamento
                </Text>
                <Text fontSize="15px" fontWeight="700" color={crm.text}>
                  {quote.number}
                </Text>
              </Box>
              <QuoteStatusPill status={quote.status} />
              {quote.approvedVersion ? (
                <Text fontSize="12px" color={crm.textMuted}>
                  Aprovado na versão {quote.approvedVersion}
                </Text>
              ) : null}
              <Flex align="center" gap="6px" ml={{ md: "auto" }}>
                <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>
                  Versão
                </Text>
                <Select
                  size="sm"
                  w="120px"
                  bg="white"
                  value={quoteVersion}
                  onChange={(e) => changeVersion(Number(e.target.value))}
                >
                  {quote.versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version}
                      {v.version === quote.currentVersion ? " (atual)" : ""}
                    </option>
                  ))}
                </Select>
                <Button size="sm" variant="outline" onClick={quoteModal.onOpen}>
                  Dados
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FaSave />}
                  bg={crm.brand}
                  color="white"
                  _hover={{ bg: crm.brandDark }}
                  isDisabled={!ready}
                  isLoading={savingVersion}
                  onClick={saveNewVersion}
                >
                  Salvar nova versão
                </Button>
              </Flex>
            </Flex>
          )}
          <SaveQuoteModal
            isOpen={quoteModal.isOpen}
            onClose={quoteModal.onClose}
            initial={quoteInitial}
            editing={Boolean(quote)}
            onSubmit={submitQuoteModal}
          />
          <AlertDialog isOpen={discardDialog.isOpen} leastDestructiveRef={keepEditingRef} onClose={discardDialog.onClose} isCentered>
            <AlertDialogOverlay>
              <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">Descartar alterações?</AlertDialogHeader>
                <AlertDialogBody>
                  Há alterações na simulação que não foram salvas na v{quoteVersion}. Se trocar para a v{pendingVersion}, elas serão perdidas.
                  Use "Salvar nova versão" para guardá-las.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={keepEditingRef} variant="ghost" onClick={discardDialog.onClose}>
                    Continuar editando
                  </Button>
                  <Button colorScheme="red" ml={3} onClick={confirmDiscard}>
                    Descartar e trocar
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <Card title="Estimativa">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
              <Field label="CVF Nº">{input(number, setNumber)}</Field>
              <Field label="Cliente">{input(client, setClient)}</Field>
              <Field label="Produto">{input(product, setProduct, { placeholder: "Máquinas e equipamentos" })}</Field>
              <Field label="Origem">{input(origin, setOrigin, { placeholder: "China" })}</Field>
              <Field label="Modal">
                <Select size="sm" bg="white" value={modal} onChange={(e) => setModal(e.target.value as Modal)}>
                  <option value="MARITIMO">Marítimo</option>
                  <option value="AEREO">Aéreo</option>
                </Select>
              </Field>
              <Field label="Incoterm">{input(incoterm, setIncoterm)}</Field>
              <Field label="Regime tributário" hint={regime === "REAL" ? "Credita PIS/COFINS" : "Sem crédito de PIS/COFINS"}>
                <Select size="sm" bg="white" value={regime} onChange={(e) => setRegime(e.target.value as Regime)}>
                  {(Object.keys(regimeLabels) as Regime[]).map((r) => (
                    <option key={r} value={r}>
                      {regimeLabels[r]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Equipamento">{input(equipment, setEquipment, { placeholder: "1 x 40HC" })}</Field>
            </SimpleGrid>
          </Card>

          <Card title="Operação">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
              <Field label="Moeda">
                <Select size="sm" bg="white" value={currency} onChange={(e) => changeCurrency(e.target.value as Currency)}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="CNY">CNY</option>
                </Select>
              </Field>
              <Field label={`Taxa fiscal ${currency} (R$)`}>{input(exchange, setExchange)}</Field>
              <Field label={`Frete internacional (${currency})`} hint="Rateado pelo peso">
                {input(freight, setFreight, { placeholder: "0,00" })}
              </Field>
              <Field label="Seguro (% de FOB + frete)" hint="Rateado pelo valor">
                {input(paramDrafts.insuranceRate, (v) => setParamDrafts((p) => ({ ...p, insuranceRate: v })))}
              </Field>
            </SimpleGrid>
          </Card>

          <Card title="Itens" subtitle="Ao informar o NCM, II/IPI/PIS/COFINS vêm da tabela. Edite para Ex-tarifário ou exceção.">
            <TableCard>
              <Thead>
                <Tr>
                  <Th {...thProps}>NCM</Th>
                  <Th {...thProps}>Descrição</Th>
                  <Th {...thProps}>Ref. PI</Th>
                  <Th {...thProps}>Qtd</Th>
                  <Th {...thProps}>Valor unit. ({currency})</Th>
                  <Th {...thProps}>Peso unit. (kg)</Th>
                  <Th {...thProps}>II %</Th>
                  <Th {...thProps}>IPI %</Th>
                  <Th {...thProps}>PIS %</Th>
                  <Th {...thProps}>COFINS %</Th>
                  <Th {...thProps}>Dumping ({currency})</Th>
                  {(coSetting || hasItemIcms) && <Th {...thProps}>Crédito ICMS %</Th>}
                  {(coSetting || hasItemIcms) && <Th {...thProps}>Recolhe ICMS %</Th>}
                  <Th {...thProps} isNumeric>CIF</Th>
                  <Th {...thProps} isNumeric>Custo net / un.</Th>
                  <Th {...thProps} />
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item, index) => {
                  const calc = result.items[index];
                  return (
                    <Tr key={item.key}>
                      <Td {...tdProps}>
                        <Input
                          size="sm"
                          w="110px"
                          value={item.ncm}
                          placeholder="0000.00.00"
                          onChange={(e) => updateItem(item.key, { ncm: e.target.value })}
                          onBlur={() => lookupNcm(item)}
                        />
                        {item.rates === null && (
                          <Box mt="4px">
                            <Pill {...toneColors.warning}>NCM sem tabela</Pill>
                          </Box>
                        )}
                        {item.rates && (item.rates.iiHasExceptions || item.rates.ipiHasExceptions) && (
                          <Box mt="4px">
                            <Pill {...toneColors.warning}>tem Ex</Pill>
                          </Box>
                        )}
                      </Td>
                      <Td {...tdProps}>
                        <Input size="sm" minW="160px" value={item.description} onChange={(e) => updateItem(item.key, { description: e.target.value })} />
                      </Td>
                      <Td {...tdProps}>
                        <Input size="sm" w="90px" value={item.reference} onChange={(e) => updateItem(item.key, { reference: e.target.value })} />
                      </Td>
                      <Td {...tdProps}>
                        <Input size="sm" w="64px" value={item.quantity} onChange={(e) => updateItem(item.key, { quantity: e.target.value })} />
                      </Td>
                      <Td {...tdProps}>
                        <Input size="sm" w="110px" value={item.unitValue} placeholder="0,00" onChange={(e) => updateItem(item.key, { unitValue: e.target.value })} />
                      </Td>
                      <Td {...tdProps}>
                        <Input size="sm" w="90px" value={item.unitWeight} placeholder="0" onChange={(e) => updateItem(item.key, { unitWeight: e.target.value })} />
                      </Td>
                      {(["ii", "ipi", "pis", "cofins"] as const).map((field) => (
                        <Td {...tdProps} key={field}>
                          <Input size="sm" w="64px" value={item[field]} onChange={(e) => updateItem(item.key, { [field]: e.target.value })} />
                        </Td>
                      ))}
                      <Td {...tdProps}>
                        <Input size="sm" w="90px" value={item.dumping} placeholder="0,00" onChange={(e) => updateItem(item.key, { dumping: e.target.value })} />
                      </Td>
                      {(coSetting || hasItemIcms) && (["icmsCredit", "icmsCollect"] as const).map((field) => (
                        <Td {...tdProps} key={field}>
                          <Input
                            size="sm"
                            w="70px"
                            value={item[field] ?? ""}
                            placeholder={toDraft(field === "icmsCredit" ? params.icmsCreditRate : params.icmsCollectRate)}
                            onChange={(e) => updateItem(item.key, { [field]: e.target.value })}
                          />
                        </Td>
                      ))}
                      <Td {...tdProps} isNumeric whiteSpace="nowrap">
                        {formatMoney(calc?.cif ?? 0, currency)}
                      </Td>
                      <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight="600">
                        {brlOf(calc?.finalCostPerUnit ?? 0)}
                      </Td>
                      <Td {...tdProps}>
                        <IconButton
                          aria-label="Remover item"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<FaTrash />}
                          isDisabled={items.length === 1}
                          onClick={() => setItems((list) => list.filter((i) => i.key !== item.key))}
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </TableCard>
            <Button mt="12px" size="sm" leftIcon={<FaPlus />} variant="outline" onClick={() => setItems((list) => [...list, newItem()])}>
              Adicionar item
            </Button>
          </Card>

          <Card title="Despesas aduaneiras (R$)" subtitle="Rateadas pelo valor. Campos com valor sugerido em cinza: deixe vazio para usar o cálculo automático.">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing="12px" alignItems="end">
              <Field label="Porto (armazenagem)">
                <Select size="sm" bg="white" value={portId} onChange={(e) => changePort(e.target.value)}>
                  {PORTS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.noData ? " (sem tarifa)" : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={port.id === CUSTOM_PORT ? "% do CIF" : `1º período % CIF${port.firstPeriodDays ? ` (${port.firstPeriodDays} dias)` : ""}`}>
                {input(storageFirst, setStorageFirst, { isDisabled: port.noData })}
              </Field>
              <Field label={port.id === CUSTOM_PORT ? "Fixo (R$)" : "Levante e pesagem (R$)"}>
                {input(storageFixed, setStorageFixed, { isDisabled: port.noData })}
              </Field>
              <Field label="Período adicional % CIF">{input(storageExtraRate, setStorageExtraRate, { isDisabled: port.noData })}</Field>
              <Field label="Nº períodos adicionais">{input(storageExtraPeriods, setStorageExtraPeriods, { isDisabled: port.noData })}</Field>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px" mt="16px">
              <Field label="Armazenagem e movimentação" hint={port.noData ? "Porto sem tarifa: informe o valor" : `CIF R$ ${fmt(cifBrl)}`}>
                {input(storageManual, setStorageManual, { placeholder: fmt(storageAuto) })}
              </Field>
              <Field label="Taxas de destino agente de carga">{input(agentFees, setAgentFees)}</Field>
              <Field label="Honorários despachante">{input(brokerFees, setBrokerFees)}</Field>
              <Field label="AFRMM" hint={modal === "MARITIMO" ? `Frete R$ × ${fmt(params.afrmmRate)}% + ${fmt(params.afrmmFixedBrl)}` : "Aéreo: sem AFRMM"}>
                {input(afrmmManual, setAfrmmManual, { placeholder: fmt(afrmmAuto) })}
              </Field>
              <Field label="Frete nacional">{input(nationalFreight, setNationalFreight, { placeholder: "0,00" })}</Field>
              <Field label="Daytona">{input(daytona, setDaytona, { placeholder: "0,00" })}</Field>
              <Field label="Licença de importação">{input(license, setLicense, { placeholder: "0,00" })}</Field>
              <Flex gap="8px">
                <Field label="Adições" hint={`Auto: ${autoAdditions} (NCMs)`}>
                  {input(additions, setAdditions, { placeholder: String(autoAdditions), w: "70px" })}
                </Field>
                <Box flex="1">
                  <Field label="Taxa SISCOMEX">{input(siscomexManual, setSiscomexManual, { placeholder: fmt(siscomexAuto) })}</Field>
                </Box>
              </Flex>
            </SimpleGrid>
          </Card>

          <Card
            title="Parâmetros"
            subtitle="Padrões da planilha. Alterações valem só para esta simulação."
            action={
              <Button size="sm" variant="ghost" rightIcon={showParams ? <FaChevronUp /> : <FaChevronDown />} onClick={() => setShowParams((s) => !s)}>
                {showParams ? "Ocultar" : "Mostrar"}
              </Button>
            }
          >
            <Collapse in={showParams} animateOpacity>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px">
                {(
                  [
                    ["cvfFeeRate", "Honorários CVF (%)"],
                    ["icmsCollectRate", "Recolhimento ICMS + fundos (%)"],
                    ["icmsCreditRate", "Crédito de ICMS (%)"],
                    ["pisCreditRate", "Crédito PIS — Lucro Real (%)"],
                    ["cofinsCreditRate", "Crédito COFINS — Lucro Real (%)"],
                    ["afrmmRate", "AFRMM (% do frete)"],
                    ["afrmmFixedBrl", "AFRMM fixo (R$)"],
                  ] as [keyof ContaOrdemParams, string][]
                ).map(([key, label]) => (
                  <Field key={key} label={label}>
                    {input(paramDrafts[key], (v) => setParamDrafts((p) => ({ ...p, [key]: v })), key === "icmsCreditRate" && creditInvalid ? { isInvalid: true } : {})}
                    {key === "icmsCreditRate" && creditInvalid && (
                      <Text fontSize="11px" color={crm.danger} mt="2px">
                        Informe um valor entre 0 e 99,99%.
                      </Text>
                    )}
                  </Field>
                ))}
              </SimpleGrid>
              <Button mt="12px" size="xs" variant="link" color={crm.brand} onClick={() => setParamDrafts(initialParams)}>
                Restaurar padrões
              </Button>
            </Collapse>
          </Card>

          {ready ? (
            <Card title="Resultado" subtitle={`Taxa fiscal ${currency} ${fmt(exchangeRate, 4)} · ${regimeLabels[regime]}`}>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px" mb="20px">
                <Stat label="Valor CIF" value={brlOf(result.totals.cif)} hint={formatMoney(result.totals.cif, currency)} />
                <Stat label="Total de tributos" value={brlOf(result.totals.taxes)} hint={`ICMS via trading ${brlOf(result.totals.icms)}`} />
                <Stat label="Desembolso na importação" value={brlOf(result.totals.disbursement)} hint={`Despesas ${formatMoney(expensesBrl, "BRL")}`} />
                <Stat label="Custo final c/ créditos" value={brlOf(result.totals.finalCost)} highlight hint={formatMoney(result.totals.finalCost, currency)} />
              </SimpleGrid>
              <EstimateSheet
                header={header}
                result={result}
                items={itemInfos}
                expenses={expenses}
                params={sheetParams}
                currency={currency}
                exchangeRate={exchangeRate}
                showInternal
              />
            </Card>
          ) : (
            <Box w="95%" p="20px" textAlign="center" color={crm.textMuted} fontSize="14px">
              {creditInvalid
                ? "Crédito de ICMS inválido nos Parâmetros: informe um valor entre 0 e 99,99%."
                : "Informe a taxa fiscal e o valor de ao menos um item para ver a estimativa."}
            </Box>
          )}

          </>)}

          {/* Versão de impressão: fora da tela, sem linhas internas */}
          <Box position="fixed" left="-10000px" top="0" w="794px" aria-hidden>
            <Box ref={printRef} p="8px" bg="white" color="black">
              {ready && (
                <EstimateSheet
                  header={header}
                  result={result}
                  items={itemInfos}
                  expenses={expenses}
                  params={sheetParams}
                  currency={currency}
                  exchangeRate={exchangeRate}
                  print
                />
              )}
            </Box>
          </Box>
        </Flex>
      </Layout>
    </Box>
  );
};

export default Simulator;
